import type { ReactNode } from "react";

const TONE = {
  ivory: "bg-ivory",
  "ivory-deep": "bg-ivory-deep",
  card: "bg-card",
} as const;

interface SectionProps {
  id?: string;
  eyebrow?: string;
  title?: string;
  lede?: string;
  tone?: keyof typeof TONE;
  children: ReactNode;
}

/** A page section: eyebrow, ruled heading, content, on the site's 12-column grid. */
export function Section({
  id,
  eyebrow,
  title,
  lede,
  tone = "ivory",
  children,
}: SectionProps) {
  // The header's weight sets the gap below it: a ruled heading commands more
  // air than a lone eyebrow, and a section with no header at all needs none.
  const contentGap =
    title || lede ? "mt-10 md:mt-12" : eyebrow ? "mt-6 md:mt-8" : "";
  return (
    <section id={id} className={`${TONE[tone]} scroll-mt-28 py-16 md:py-24`}>
      {/* Padding sits inside the width container, as it does in the header,
          the footer and the page heading, so every column starts on the
          same line. */}
      <div className="mx-auto max-w-6xl px-5 md:px-10">
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        {title ? (
          <h2
            className={`font-display rule-heading text-[1.85rem] text-ink md:text-[2.5rem] ${eyebrow ? "mt-3" : ""}`}
          >
            {title}
          </h2>
        ) : null}
        {lede ? (
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-soft md:text-lg">
            {lede}
          </p>
        ) : null}
        <div className={contentGap}>{children}</div>
      </div>
    </section>
  );
}
